import { ReactElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Cancel,
  CheckCircle,
  Contrast,
  ExpandMore,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Lock,
  PlayArrow,
  RemoveCircleOutline,
  WarningAmber,
} from '@mui/icons-material';
import { ViewToolbar } from '@/components/ViewToolbar';
import { useHasPermission } from '@/components/PermissionGate';
import { formatDateTime } from '@/lib/locale';
import { CopyPlanScenarioDialog } from './CopyPlanScenarioDialog';
import { CopyPlanVersionDialog } from './CopyPlanVersionDialog';
import {
  BudgetSetupStatus,
  SetupInstance,
  SetupPhase,
  SetupStepResult,
  useBudgetSetupStatus,
} from './hooks/useBudgetSetupStatus';

/**
 * The BudgetSetup hub (spec: docs/15-designspecs/Setup-Flows.md, Phase 1) — a read-only page listing
 * the steps needed for a working budget, with statuses computed live from the data by
 * GET /api/finance/budgetsetup/status. The hub owns no forms and never mutates; every step deep-links
 * into the existing view where the work is done.
 *
 * Hand-written for Phase 1, but rendered entirely from the backend's flow-definition response so the
 * Phase-2 "one Core shell + generated config" split has a ready shape to lift out.
 */

// Which matrix row is expanded survives leaving the page (same localStorage pattern as the theme
// preference). A stale instance id — e.g. a version that has since been published away — simply
// matches no row and is ignored.
const EXPANDED_STORAGE_KEY = 'nexo.budgetSetup.expandedInstance';

// INCOMPLETE splits visually on whether any progress exists: with data but zero progress it renders
// as an error ("Fel"), with partial progress as a half-done state ("Delvis"). NOT_STARTED is the
// no-data-at-all case. There is no persisted history — "previously green" is not expressible.
type VisualStatus = 'complete' | 'partial' | 'failed' | 'warning' | 'notStarted' | 'blocked';

function visualStatus(step: SetupStepResult): VisualStatus {
  switch (step.status) {
    case 'COMPLETE':
      return 'complete';
    case 'WARNING':
      return 'warning';
    case 'BLOCKED':
      return 'blocked';
    case 'NOT_STARTED':
      return 'notStarted';
    case 'INCOMPLETE':
    default:
      return step.actualCount > 0 ? 'partial' : 'failed';
  }
}

function StatusIcon({ step }: { step: SetupStepResult }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const visual = visualStatus(step);

  const icon = {
    complete: <CheckCircle fontSize="small" sx={{ color: theme.palette.success.main }} />,
    partial: <Contrast fontSize="small" sx={{ color: theme.palette.warning.main }} />,
    failed: <Cancel fontSize="small" sx={{ color: theme.palette.error.main }} />,
    warning: <WarningAmber fontSize="small" sx={{ color: theme.palette.warning.main }} />,
    notStarted: <RemoveCircleOutline fontSize="small" sx={{ color: theme.palette.text.disabled }} />,
    blocked: <Lock fontSize="small" sx={{ color: theme.palette.text.disabled }} />,
  }[visual];

  const tooltip = step.evaluationMs > 0
    ? `${step.reason} · ${step.evaluationMs} ms`
    : step.reason || t(`Finance.BudgetSetup.StatusChip.${visual}`);

  return (
    <Tooltip title={tooltip} arrow>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</Box>
    </Tooltip>
  );
}

function StatusChip({ step }: { step: SetupStepResult }) {
  const { t } = useTranslation();
  const visual = visualStatus(step);
  const color = {
    complete: 'success',
    partial: 'warning',
    failed: 'error',
    warning: 'warning',
    notStarted: 'default',
    blocked: 'default',
  }[visual] as 'success' | 'warning' | 'error' | 'default';

  return (
    <Chip
      size="small"
      color={color}
      variant={visual === 'complete' ? 'filled' : 'outlined'}
      label={t(`Finance.BudgetSetup.StatusChip.${visual}`)}
    />
  );
}

function StartHereChip() {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      color="primary"
      icon={<PlayArrow />}
      label={t('Finance.BudgetSetup.StartHere')}
    />
  );
}

function StepRow({ step, isNextAction }: { step: SetupStepResult; isNextAction: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="flex-start"
      sx={{ py: 1.5, px: 1, borderTop: 1, borderColor: 'divider' }}
    >
      <Box sx={{ pt: 0.25 }}>
        <StatusIcon step={step} />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2">{step.title}</Typography>
          <StatusChip step={step} />
          {step.advisory && (
            <Typography variant="caption" color="text.secondary">
              {t('Finance.BudgetSetup.Advisory')}
            </Typography>
          )}
          {isNextAction && <StartHereChip />}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {step.reason || step.description}
        </Typography>
        {step.links.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {step.links.map((link) => (
              <Chip
                key={link.route}
                size="small"
                variant="outlined"
                color="primary"
                clickable
                label={`${link.label} →`}
                onClick={(e) => { e.stopPropagation(); navigate(link.route); }}
              />
            ))}
          </Stack>
        )}
      </Box>
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          navigate(step.route, step.routePreFill ? { state: { preFill: step.routePreFill } } : undefined);
        }}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {step.actionLabel ?? t('Finance.BudgetSetup.Open', { title: step.routeLabel || step.title })}
      </Button>
    </Stack>
  );
}

function phaseCounts(steps: SetupStepResult[]) {
  const counted = steps.filter((s) => !s.advisory);
  return {
    done: counted.filter((s) => s.status === 'COMPLETE').length,
    total: counted.length,
    warnings: steps.filter((s) => s.status === 'WARNING').length,
  };
}

function OneTimeSection({ phase, nextActionStepId }: { phase: SetupPhase; nextActionStepId: string | null }) {
  const { t } = useTranslation();
  const counts = phaseCounts(phase.steps);
  const allComplete = counts.done === counts.total && counts.warnings === 0;

  return (
    <Accordion defaultExpanded={!allComplete} disableGutters>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1, pr: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{phase.title}</Typography>
          <Chip size="small" variant="outlined" label={t('Finance.BudgetSetup.OneTime')} />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color={allComplete ? 'success.main' : 'text.secondary'}>
            {t('Finance.BudgetSetup.CompletedOfTotal', { done: counts.done, total: counts.total })}
            {counts.warnings > 0 && ` · ${t('Finance.BudgetSetup.WarningCount', { count: counts.warnings })}`}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {phase.steps.map((step) => (
          <StepRow key={step.stepId} step={step} isNextAction={step.stepId === nextActionStepId} />
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * The budget-cycle matrix: one row per DRAFT PlanVersion, grouped under its PlanScenario's header row.
 * The scenario row carries the cycle-start status (draft version exists); locked/published versions are
 * never in the response. Columns are the entry-readiness steps.
 */
function CycleSection({
  cycle,
  entry,
  summary,
  onChanged,
}: {
  cycle: SetupPhase;
  entry: SetupPhase;
  summary: BudgetSetupStatus['summary'];
  /** Called after a quick action mutated data (the hub re-evaluates). */
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // The cycle-start quickAction (CopyPlanVersion): the hub only OPENS the command surface — it
  // never mutates itself. Client-side permission gating mirrors the server's [Authorize].
  const canCopyVersion = useHasPermission('Finance.PlanVersion.CopyPlanVersion');
  const [copySource, setCopySource] = useState<{ id: string; label: string } | null>(null);

  // The phase-level quickAction (CopyPlanScenario) renders only in the zero-instance empty state —
  // it never competes with the per-instance CopyPlanVersion button above.
  const canCopyScenario = useHasPermission('Finance.PlanScenario.CopyPlanScenario');
  const [copyScenarioSource, setCopyScenarioSource] = useState<string | null>(null);

  // Click-to-drill: a matrix cell answers "what state?", the expanded row answers "why, and what do
  // I do about it?" — full step rows with reason text and open buttons, per the mockup's detail view.
  const [expandedId, setExpandedId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(EXPANDED_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const toggleExpanded = (id: string) => setExpandedId((prev) => {
    const next = prev === id ? null : id;
    try {
      if (next) {
        localStorage.setItem(EXPANDED_STORAGE_KEY, next);
      } else {
        localStorage.removeItem(EXPANDED_STORAGE_KEY);
      }
    } catch {
      // Private mode / storage quota — expansion still works for the session.
    }
    return next;
  });

  const versionsByScenario = useMemo(() => {
    const map = new Map<string, SetupInstance[]>();
    for (const instance of entry.instances) {
      const key = instance.parentInstanceId ?? '';
      map.set(key, [...(map.get(key) ?? []), instance]);
    }
    return map;
  }, [entry.instances]);

  const stepColumns = entry.instances[0]?.steps ?? [];
  const allSteps = [
    ...cycle.instances.flatMap((i) => i.steps),
    ...entry.instances.flatMap((i) => i.steps),
  ];
  const counts = phaseCounts(allSteps);
  const allComplete = counts.total > 0 && counts.done === counts.total && counts.warnings === 0;

  return (
    <Accordion defaultExpanded={!allComplete} disableGutters>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1, pr: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('Finance.BudgetSetup.CycleSection')}
          </Typography>
          <Chip size="small" variant="outlined" label={t('Finance.BudgetSetup.PerInstance')} />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color={allComplete ? 'success.main' : 'text.secondary'}>
            {t('Finance.BudgetSetup.CompletedOfTotal', { done: counts.done, total: counts.total })}
            {counts.warnings > 0 && ` · ${t('Finance.BudgetSetup.WarningCount', { count: counts.warnings })}`}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {cycle.instances.length === 0 ? (
          <Alert
            severity="info"
            action={
              <Stack direction="row" spacing={1}>
                {cycle.emptyStateQuickAction && canCopyScenario && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setCopyScenarioSource(cycle.emptyStateQuickAction!.sourceScenarioId)}
                  >
                    {cycle.emptyStateQuickAction.label}
                  </Button>
                )}
                {cycle.emptyStateRoute && (
                  <Button size="small" onClick={() => navigate(cycle.emptyStateRoute!)}>
                    {t('Finance.BudgetSetup.CreateFirst')}
                  </Button>
                )}
              </Stack>
            }
          >
            {t('Finance.BudgetSetup.EmptyCycle')}
          </Alert>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('Finance.BudgetSetup.ScenarioColumn')}</TableCell>
                  {stepColumns.map((step) => (
                    <TableCell key={step.stepId} align="center">{step.title}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {cycle.instances.map((scenario) => {
                  const cycleStep = scenario.steps[0];
                  const versions = versionsByScenario.get(scenario.instanceId) ?? [];
                  const scenarioExpanded = expandedId === scenario.instanceId;
                  return [
                    <TableRow
                      key={scenario.instanceId}
                      sx={{ bgcolor: 'action.hover', cursor: 'pointer' }}
                      onClick={() => toggleExpanded(scenario.instanceId)}
                    >
                      <TableCell colSpan={1 + stepColumns.length}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          {scenarioExpanded
                            ? <KeyboardArrowDown fontSize="small" sx={{ color: 'text.secondary' }} />
                            : <KeyboardArrowRight fontSize="small" sx={{ color: 'text.secondary' }} />}
                          <Typography variant="subtitle2">{scenario.label}</Typography>
                          {cycleStep && <StatusIcon step={cycleStep} />}
                          {cycleStep && (
                            <Typography variant="caption" color="text.secondary">
                              {cycleStep.reason}
                            </Typography>
                          )}
                          {cycleStep
                            && summary.nextActionStepId === cycleStep.stepId
                            && summary.nextActionInstanceId === scenario.instanceId
                            && <StartHereChip />}
                          {cycleStep?.quickAction && canCopyVersion && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCopySource({
                                  id: cycleStep.quickAction!.sourceVersionId,
                                  label: cycleStep.quickAction!.sourceVersionLabel,
                                });
                              }}
                            >
                              {cycleStep.quickAction.label}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>,
                    scenarioExpanded && (
                      <TableRow key={`${scenario.instanceId}-detail`}>
                        <TableCell colSpan={1 + stepColumns.length} sx={{ py: 0, pl: 5 }}>
                          {/* Scenario facts (type · fiscal year · date range) — the expansion is
                              about the scenario itself; version specifics live on the version rows. */}
                          {scenario.info && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                              {scenario.info}
                            </Typography>
                          )}
                          {scenario.steps.map((step) => (
                            <StepRow key={step.stepId} step={step} isNextAction={false} />
                          ))}
                        </TableCell>
                      </TableRow>
                    ),
                    ...versions.flatMap((version) => {
                      const versionExpanded = expandedId === version.instanceId;
                      return [
                        <TableRow
                          key={version.instanceId}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => toggleExpanded(version.instanceId)}
                        >
                          <TableCell sx={{ pl: 4 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {versionExpanded
                                ? <KeyboardArrowDown fontSize="small" sx={{ color: 'text.secondary' }} />
                                : <KeyboardArrowRight fontSize="small" sx={{ color: 'text.secondary' }} />}
                              <Typography variant="body2">{version.label}</Typography>
                            </Stack>
                          </TableCell>
                          {version.steps.map((step) => (
                            <TableCell key={step.stepId} align="center">
                              <StatusIcon step={step} />
                            </TableCell>
                          ))}
                        </TableRow>,
                        versionExpanded && (
                          <TableRow key={`${version.instanceId}-detail`}>
                            <TableCell colSpan={1 + stepColumns.length} sx={{ py: 0, pl: 5 }}>
                              {version.steps.map((step) => (
                                <StepRow key={step.stepId} step={step} isNextAction={false} />
                              ))}
                            </TableCell>
                          </TableRow>
                        ),
                      ];
                    }),
                  ];
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        {copySource && (
          <CopyPlanVersionDialog
            entityId={copySource.id}
            open
            onClose={() => setCopySource(null)}
            onCopied={onChanged}
          />
        )}
        {copyScenarioSource && (
          <CopyPlanScenarioDialog
            entityId={copyScenarioSource}
            open
            onClose={() => setCopyScenarioSource(null)}
            onCopied={onChanged}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function Legend() {
  const { t } = useTranslation();
  const theme = useTheme();
  const entries: { icon: ReactElement; key: string }[] = [
    { icon: <CheckCircle fontSize="inherit" sx={{ color: theme.palette.success.main }} />, key: 'complete' },
    { icon: <Contrast fontSize="inherit" sx={{ color: theme.palette.warning.main }} />, key: 'partial' },
    { icon: <WarningAmber fontSize="inherit" sx={{ color: theme.palette.warning.main }} />, key: 'warning' },
    { icon: <Cancel fontSize="inherit" sx={{ color: theme.palette.error.main }} />, key: 'failed' },
    { icon: <RemoveCircleOutline fontSize="inherit" sx={{ color: theme.palette.text.disabled }} />, key: 'notStarted' },
    { icon: <Lock fontSize="inherit" sx={{ color: theme.palette.text.disabled }} />, key: 'blocked' },
    { icon: <PlayArrow fontSize="inherit" color="primary" />, key: 'startHere' },
  ];
  return (
    <Stack
      direction="row"
      spacing={2}
      flexWrap="wrap"
      useFlexGap
      justifyContent="center"
      sx={{ py: 2, color: 'text.secondary', fontSize: 13 }}
    >
      {entries.map((entry) => (
        <Stack key={entry.key} direction="row" spacing={0.5} alignItems="center">
          {entry.icon}
          <span>{t(`Finance.BudgetSetup.Legend.${entry.key}`)}</span>
        </Stack>
      ))}
    </Stack>
  );
}

function HeaderCard({ data, onReEvaluate }: { data: BudgetSetupStatus; onReEvaluate: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { summary } = data;
  const ratio = summary.totalUnits > 0 ? (summary.completedUnits / summary.totalUnits) * 100 : 0;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={ratio}
            size={64}
            color={ratio >= 100 ? 'success' : 'primary'}
          />
          <Box
            sx={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {summary.completedUnits}/{summary.totalUnits}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 260 }}>
          <Typography variant="h6">{data.title}</Typography>
          <Typography variant="body2" color="text.secondary">{data.description}</Typography>
          {summary.worstStepTitle && (
            <Typography variant="body2" sx={{ mt: 0.5 }} color="warning.main">
              {summary.worstInstanceLabel ? `${summary.worstInstanceLabel}: ` : ''}
              {summary.worstStepTitle}
              {summary.worstReason ? ` — ${summary.worstReason}` : ''}
            </Typography>
          )}
        </Box>
        <Stack spacing={1} alignItems="flex-end">
          <Typography variant="caption" color="text.secondary">
            {t('Finance.BudgetSetup.LastEvaluated', {
              time: formatDateTime(new Date(data.evaluatedAtUtc)),
            })}
          </Typography>
          <Stack direction="row" spacing={1}>
            {summary.nextActionRoute && summary.nextActionTitle && (
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={() => navigate(summary.nextActionRoute!)}
              >
                {summary.nextActionTitle}
              </Button>
            )}
            <Button size="small" variant="outlined" onClick={onReEvaluate}>
              {t('Finance.BudgetSetup.ReEvaluate')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}

export function BudgetSetupView() {
  const { data, isLoading, error, refetch } = useBudgetSetupStatus();

  const cycle = data?.phases.find((p) => p.id === 'cycleStart');
  const entry = data?.phases.find((p) => p.id === 'entryReadiness');
  const oneTimePhases = data?.phases.filter((p) => p.repetition === 'ONE_TIME') ?? [];

  return (
    <Box>
      <ViewToolbar showBackButton onRefresh={() => refetch()} />
      <Box sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error.message}</Alert>}
        {data && (
          <>
            <HeaderCard data={data} onReEvaluate={() => refetch()} />
            <Stack spacing={1.5}>
              {oneTimePhases.map((phase) => (
                <OneTimeSection
                  key={phase.id}
                  phase={phase}
                  nextActionStepId={data.summary.nextActionInstanceId ? null : data.summary.nextActionStepId}
                />
              ))}
              {cycle && entry && (
                <CycleSection
                  cycle={cycle}
                  entry={entry}
                  summary={data.summary}
                  onChanged={() => refetch()}
                />
              )}
            </Stack>
            <Legend />
          </>
        )}
      </Box>
    </Box>
  );
}
